import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAnnouncement } from '../../contexts/AnnouncementContext';
import { openAnnouncementTarget } from '../../utils/announcementTarget';

const AnnouncementPopup: React.FC = () => {
  const { announcement, isOpen, closeAnnouncement } = useAnnouncement();
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [openingLink, setOpeningLink] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        void closeAnnouncement('dismissed');
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, closeAnnouncement]);

  if (!isOpen || !announcement) return null;

  const handleCta = async () => {
    try {
      setOpeningLink(true);
      await openAnnouncementTarget(announcement.target_url, announcement.attach_jwt);
      await closeAnnouncement('cta');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to open announcement link');
    } finally {
      setOpeningLink(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/50 p-4 sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) void closeAnnouncement('dismissed');
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Announcement"
        className="relative flex max-h-[85vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={() => void closeAnnouncement('dismissed')}
          aria-label="Dismiss announcement"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/65 text-white shadow-md backdrop-blur-sm transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X size={18} />
        </button>

        <div className="min-h-0 flex-1 overflow-auto bg-[#f8f7f2]">
          <img
            src={announcement.image_url}
            alt="Announcement"
            className="mx-auto block max-h-[65vh] w-full object-contain"
          />
        </div>

        <footer className="shrink-0 border-t border-gray-100 bg-white p-4 sm:p-5">
          <button
            type="button"
            disabled={openingLink}
            onClick={() => void handleCta()}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
          >
            {openingLink ? 'Opening…' : announcement.button_label}
            <ArrowUpRight size={16} />
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AnnouncementPopup;
